import * as THREE from 'three';
import { PERSON_LANDMARKS as P } from './anatomy.js';
import { PersonHairMaterial } from './lighting.js';
import { strandFlow, curlRelief } from './hairDesign.js';

const smooth = THREE.MathUtils.smoothstep;

// One shared, broad highlight for hair. A low exponent keeps the reflection
// spread across the volume, including dark hair, instead of tiny bright points.
export function hairMaterial() {
  return new PersonHairMaterial({
    name:'person-hair', color:0xffffff, vertexColors:true,
    specular:'#242424', shininess:12
  });
}

// Painted contact shading is independent of light/camera and travels with the
// articulated skin. Reuses existing vertex colors, preserving player palettes.
export function finishSkin(geo, region) {
  const p=geo.attributes.position, values=new Float32Array(p.count*3);
  for(let i=0;i<p.count;i++) {
    const x=p.getX(i), y=p.getY(i), z=p.getZ(i);
    let value=1, warmth=0;
    if(region==='head') {
      value=.86+.14*smooth(y,-.46,-.26); // neck under the jaw
      const cheeks=Math.exp(-(((Math.abs(x)-.26)/.13)**2+((y+.12)/.14)**2))*smooth(z,.16,.29);
      const ear=smooth(Math.abs(x),.36,.40)*(1-smooth(Math.abs(y),.07,.14));
      warmth=Math.max(cheeks,ear)*.7;
    } else if(region==='arm') {
      value=1-.07*Math.exp(-(((y+.59)/.065)**2));
      warmth=.35*(1-smooth(y,-.68,-.60));
    } else if(region==='leg') {
      value=.94+.06*smooth(y,-.85,-.65);
    } else if(region==='foot') {
      value=.90+.10*smooth(y,-.045,.06);
    }
    values[i*3]=value;
    values[i*3+1]=value*(1-.035*warmth);
    values[i*3+2]=value*(1-.065*warmth);
  }
  geo.setAttribute('color',new THREE.BufferAttribute(values,3));
  return geo;
}

export function finishHair(geo, style) {
  const p=geo.attributes.position,n=geo.attributes.normal,values=new Float32Array(p.count*3);
  for(let i=0;i<p.count;i++) {
    const x=p.getX(i),y=p.getY(i)-P.headY,z=p.getZ(i);
    // Roots and long-hair undersides retain depth under ambient city lighting.
    const crown=smooth(y,-.2,.32);
    const flow=strandFlow(style,x,y,z);
    let value=.79+.15*crown+.06*flow;
    if(style==='short') {
      value=.73+.13*crown+.14*flow;
    }
    if(style==='pompadour') {
      const part=Math.exp(-Math.pow((x-.20-.12*z)/.036,2))*smooth(y,.12,.3);
      value=.73+.10*crown+.17*flow-.08*part;
    }
    if(style==='long') {
      const part=Math.exp(-Math.pow(x/.035,2))*smooth(z,.04,.2)*smooth(y,.13,.3);
      value=.71+.12*crown+.17*flow-.12*part;
    }
    if(style==='curly')value=.72+.20*Math.min(1,curlRelief(x,y,z)/.095)+.08*crown;
    if(style==='spiky')value=.72+.20*smooth(y,.08,.65)+.08*flow;
    if(style==='long'&&y<-.25) {
      const outward=x*n.getX(i)+(z+.26)*n.getZ(i);
      value*=outward<0?.73:.95;
    }
    values.fill(value,i*3,i*3+3);
  }
  geo.setAttribute('color',new THREE.BufferAttribute(values,3));
  return geo;
}
